// prettier-ignore
import gql from 'graphql-tag';

import { anyone, adminOnly, nonParent } from './access_defs';
import { gql_fetcher } from './buildGqlQuery';
import prependAcFn from './prependAcFn';

// Every toolkit link on a page is checked (AccessControl's Link), so a list of
// resources used to ask one `toolkitsResource` each: hundreds of checks in one
// batch, which the graphql service answers with a request to myf2b apiece, all at
// once (TECH-487). Asking once for everything the user may open, and answering each
// link from that, makes it one request. myf2b decides both the same way: the
// resource is among the user's licence component resources.
const TOOLKITS_RESOURCES = gql`query ACCESS_CONTROL__toolkitsResources{ me { access { toolkitsResources { type slug } } } }`;

// Long enough to cover a page and the next few clicks, short enough that a licence
// change shows up without a reload.
const CACHE_MS = 60 * 1000;

// MySQL compares type and slug case-insensitively, so this does too.
const resourceKey = (type, slug) => `${type}:${slug}`.toLowerCase();

const cache = new Map();

const accessibleResources = (user) => {
	const userKey = user?.id ?? '';
	const cached = cache.get(userKey);
	if(cached && Date.now() - cached.at < CACHE_MS){ return cached.keys; }

	const keys = gql_fetcher({ document: TOOLKITS_RESOURCES, variables: {} }).then(
		(data) => new Set((data?.me?.access?.toolkitsResources ?? []).map(({ type, slug }) => resourceKey(type, slug)))
	);
	cache.set(userKey, { at: Date.now(), keys });
	// A failed lookup is not kept, so the next check asks again.
	keys.catch(() => cache.get(userKey)?.keys === keys && cache.delete(userKey));
	return keys;
};

const toolkitsAccess = (type) => async (user, { slug } = {}) => (
	!!slug && (await accessibleResources(user)).has(resourceKey(type, slug))
);

export default prependAcFn(nonParent, {
	'toolkits/assignment-playlist': adminOnly,
	'toolkits/search-sim': adminOnly,
	'toolkits/search': anyone,
	'toolkits/alignments/[slug]': toolkitsAccess('Alignment'),
	'toolkits/books/[slug]': toolkitsAccess('Book'),
	'toolkits/games/[slug]': toolkitsAccess('Game'),
	'toolkits/lessons/[slug]': toolkitsAccess('Lesson'),
	'toolkits/library/[slug]': toolkitsAccess('Page'),
	'toolkits/printables/[slug]': toolkitsAccess('Printable'),
	'toolkits/units/[slug]': toolkitsAccess('Unit'),
	'toolkits/videos/[slug]': toolkitsAccess('Video'),
	'toolkits/[[...path]]': anyone,
});
